import { Footprints, MapPin, ExternalLink } from "lucide-react";

const SHOP = "MUTHA PARAS %26 CO, 28 Venkusahuji Lane, Chickpet Cross, Bangalore 560053";

// Walking times and distances taken from Google Maps, measured to the shop door.
const ROUTES = [
  {
    id: "chickpet-circle",
    from: "Chickpet Circle",
    origin: "Chickpet Circle, Bengaluru",
    time: "5 min walk",
    distance: "300 m",
    directions: "Walk up BVK Iyengar Rd towards Chickpet Market, then turn right into MT St / DK Ln.",
  },
  {
    id: "metro-main",
    from: "Chickpete Metro — main exit",
    origin: "Chickpete Metro Station, Bengaluru",
    time: "8 min walk",
    distance: "550 m",
    directions: "Walk up BVK Iyengar Rd past Chickpet Circle, then turn right into MT St / DK Ln.",
  },
  {
    id: "metro-tharagupet",
    from: "Chickpete Metro — Tharagupet exit",
    origin: "Chickpete Metro Station Tharagupet Exit, Bengaluru",
    time: "13 min walk",
    distance: "850 m",
    directions: "Walk to BVK Iyengar Rd, head up towards Chickpet Market, then turn right into MT St / DK Ln.",
  },
  {
    id: "pothys",
    from: "Pothys, Kempegowda Rd",
    origin: "Pothys Kempegowda Road, Bengaluru",
    time: "9 min walk",
    distance: "650 m",
    directions: "Walk down BVK Iyengar Rd, then turn left into MT St / DK Ln.",
  },
  {
    id: "mysore-bank",
    from: "Mysore Bank Circle",
    origin: "Mysore Bank Circle, Bengaluru",
    time: "9 min walk",
    distance: "700 m",
    directions: "Walk down Avenue Rd, then turn right towards DK Ln.",
  },
];

const directionsUrl = (origin) =>
  `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${SHOP}&travelmode=walking`;

export const HowToReachUs = () => (
  <div className="mt-16" data-testid="how-to-reach-section">
    <div className="mb-8">
      <p
        className="text-[10px] sm:text-xs tracking-[0.2em] uppercase mb-2"
        style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
      >
        Walking routes
      </p>
      <h3
        className="text-2xl md:text-3xl font-medium"
        style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
      >
        How to reach us
      </h3>
      <p
        className="text-sm md:text-base mt-2 max-w-2xl"
        style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif", lineHeight: 1.7 }}
      >
        We're a short walk from Chickpete Metro and the main Chickpet landmarks. Tap any route to
        open live walking directions on your phone.
      </p>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
      {ROUTES.map((r) => (
        <a
          key={r.id}
          href={directionsUrl(r.origin)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`route-card-${r.id}`}
          className="group flex flex-col p-5 rounded-sm border transition-colors hover:border-[var(--mp-copper)]"
          style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-card)" }}
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <h4
              className="text-base md:text-lg font-medium leading-snug"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              From {r.from}
            </h4>
            <ExternalLink
              className="w-3.5 h-3.5 mt-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ color: "var(--mp-copper)" }}
            />
          </div>
          <p
            className="flex items-center gap-2 text-sm font-semibold mb-2.5"
            style={{ color: "var(--mp-copper-deep)", fontFamily: "'Manrope', sans-serif" }}
          >
            <Footprints className="w-4 h-4" />
            {r.time}
            <span style={{ color: "var(--mp-line)" }}>·</span>
            {r.distance}
          </p>
          <p
            className="text-sm"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif", lineHeight: 1.65 }}
          >
            {r.directions}
          </p>
        </a>
      ))}

      <div
        className="flex flex-col justify-center p-5 rounded-sm border"
        style={{ borderColor: "var(--mp-copper)", backgroundColor: "var(--mp-surface)" }}
        data-testid="route-card-shop"
      >
        <MapPin className="w-5 h-5 mb-3" style={{ color: "var(--mp-copper)" }} />
        <h4
          className="text-base md:text-lg font-medium mb-1.5"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
        >
          Our shop
        </h4>
        <p
          className="text-sm mb-4"
          style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif", lineHeight: 1.65 }}
        >
          Kochaar Mutha's Enclave, No. 28, Venkusahuji Lane, Chickpet Cross, Bangalore 560053
        </p>
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${SHOP}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="route-open-maps"
          className="inline-flex items-center gap-1.5 text-xs font-semibold underline underline-offset-4"
          style={{ color: "var(--mp-copper-deep)", fontFamily: "'Manrope', sans-serif" }}
        >
          Open in Google Maps <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>

    <p
      className="text-[11px] mt-4"
      style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
    >
      Walking times from Google Maps. Shop visits by appointment — call or WhatsApp +91 76248 53840.
    </p>
  </div>
);
